import type { IScope } from "angular";
import { finalize } from "rxjs/operators";

import { cloneDeep, isEmpty, isEqual } from "lodash";

import type { IAlarmCentral, IAlarmCentralSite, IResponseWithLoading, ISite } from "App/Modules/Models";
import { AlarmCentralType } from "App/Modules/Models";
import type { IAlarmCentralsRepository, ISitesRepository } from "App/Modules/Repositories";
import type { IFormContainerResolve } from "App/Modules/Services";
import { extendWithLoadingAndFail } from "App/Modules/Services/ObservableExtensions";

export default class EditSiteAlarmCentralsComponentController {
    public static $inject: string[] = ["$scope", "mmAlarmCentralsRepository", "mmSitesRepository"];

    public site: ISite;

    public originalSite: ISite;

    public alarmCentrals: IResponseWithLoading<IAlarmCentral[]>;

    public secondaryAlarmCentrals: IAlarmCentral[];

    public primaryAlarmCentral: IAlarmCentral;

    public secondaryAlarmCentral: IAlarmCentral;

    public originalSiteAlarmCentrals: IAlarmCentralSite[];

    public siteAlarmCentrals: IAlarmCentralSite[];

    public editResponse: IResponseWithLoading<ISite>;

    public resolve: IFormContainerResolve<ISite, void>;

    constructor(
        private readonly $scope: IScope,
        private readonly alarmCentralsRepository: IAlarmCentralsRepository,
        private readonly sitesRepository: ISitesRepository,
    ) {}

    public $onInit() {
        this.site = cloneDeep(this.resolve.formData);
        this.originalSiteAlarmCentrals = this.resolve.formData.alarmCentrals;
        this.siteAlarmCentrals = cloneDeep(this.originalSiteAlarmCentrals);
        this.originalSite = cloneDeep(this.site);

        this.setAlarmCentrals();

        extendWithLoadingAndFail(this.alarmCentralsRepository.getAlarmCentrals())
            .pipe(finalize(() => this.$scope.$applyAsync()))
            .subscribe(
                (response) => {
                    if (!response.hasError && response.data) {
                        this.alarmCentrals = response;

                        if (this.originalSiteAlarmCentrals && this.originalSiteAlarmCentrals.length > 0) {
                            this.filterSecondaryAlarmCentrals(this.primaryAlarmCentral);
                        }
                    }
                },
                (error) => {
                    this.alarmCentrals = error;
                },
            );

        this.$scope.$watch("$ctrl.primaryAlarmCentral", (newValue: IAlarmCentral, oldValue: IAlarmCentral) => {
            if (newValue !== oldValue) {
                this.filterSecondaryAlarmCentrals(newValue);
            }
        });
    }

    public cancel() {
        if (this.resolve.options.onCancel) {
            this.resolve.options.onCancel();
        }
    }

    public editSiteAlarmCentrals() {
        const patchObject = {} as ISite;
        const newSiteAlarmCentrals: IAlarmCentralSite[] = [];

        if (!isEmpty(this.primaryAlarmCentral)) {
            newSiteAlarmCentrals.push({
                alarmCentral: this.primaryAlarmCentral,
                alarmCentralType: AlarmCentralType.Primary,
            } as IAlarmCentralSite);

            if (!isEmpty(this.secondaryAlarmCentral)) {
                newSiteAlarmCentrals.push({
                    alarmCentral: this.secondaryAlarmCentral,
                    alarmCentralType: AlarmCentralType.Secondary,
                } as IAlarmCentralSite);
            }
        }

        const tertiaryAlarmCentral = this.siteAlarmCentrals.find(
            (acs) => acs.alarmCentralType === AlarmCentralType.Tertiary,
        );
        if (tertiaryAlarmCentral !== undefined) {
            newSiteAlarmCentrals.push(tertiaryAlarmCentral);
        }

        if (!isEqual(this.originalSiteAlarmCentrals, newSiteAlarmCentrals)) {
            patchObject.alarmCentrals = newSiteAlarmCentrals;
        }

        if (!isEqual(this.originalSite.externalIntegrationId, this.site.externalIntegrationId)) {
            patchObject.externalIntegrationId = this.site.externalIntegrationId;
        }

        if (!isEmpty(patchObject)) {
            extendWithLoadingAndFail(this.sitesRepository.patchSite(this.site.id, patchObject))
                .pipe(finalize(() => this.$scope.$applyAsync()))
                .subscribe(
                    (editResponse) => {
                        this.editResponse = editResponse;
                        if (!editResponse.isLoading && editResponse.data) {
                            this.resolve.options.onOk(editResponse.data);
                        }
                    },
                    (errorResponse) => {
                        this.editResponse = errorResponse;
                    },
                );
        } else {
            this.cancel();
        }
    }

    public filterSecondaryAlarmCentrals(alarmCentral: IAlarmCentral) {
        if (this.secondaryAlarmCentral && isEqual(alarmCentral, this.secondaryAlarmCentral)) {
            this.secondaryAlarmCentral = null;
        }

        if (this.primaryAlarmCentral !== null) {
            this.secondaryAlarmCentrals = this.alarmCentrals.data.filter(
                (arc) => arc.id !== this.primaryAlarmCentral.id,
            );
        }
    }

    private setAlarmCentrals(): void {
        const primaryArc = this.site.alarmCentrals.find((acs) => acs.alarmCentralType === AlarmCentralType.Primary);
        const secondaryArc = this.site.alarmCentrals.find((acs) => acs.alarmCentralType === AlarmCentralType.Secondary);

        this.primaryAlarmCentral = primaryArc !== undefined ? primaryArc.alarmCentral : (null as IAlarmCentral);
        this.secondaryAlarmCentral = secondaryArc !== undefined ? secondaryArc.alarmCentral : (null as IAlarmCentral);
    }
}
